/**
 * Design-token sources for a project.
 *
 * Uploading a tokens file is the path that works on EVERY Figma plan. Reading
 * design variables straight out of Figma over REST requires a Figma Enterprise
 * plan, so for nearly every customer the automatic path is closed by Figma, not
 * by us. This client is how the dashboard offers the path that is open.
 */
import { fetchApi } from './client';
import { log } from '@/lib/logger';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

export interface TokenSource {
  id: string;
  kind: import('@overlayqa/shared').TokenSourceKind;
  label: string;
  /** The user's selection. Only enabled sources feed the Design System Audit. */
  enabled: boolean;
  tokenCount: number;
  createdAt: string;
}

/**
 * Every function takes the Clerk token FIRST, the same convention as
 * projectsApi. This module shipped without passing one, so every call left
 * the browser unauthenticated and 401'd (caught by the project-token-sources
 * journey 2026-08-11); the api-boundary tests now pin the token on each call.
 */
export const tokenSourcesApi = {
  list: (token: string | null, projectId: string): Promise<TokenSource[]> =>
    fetchApi<TokenSource[]>(`/projects/${projectId}/token-sources`, { token: token ?? undefined, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  /**
   * `tokens` is the PARSED contents of the user's file, not the File object.
   * Reading it in the browser keeps multipart handling off the server and lets
   * us reject an unreadable file before it ever leaves the page.
   */
  upload: (token: string | null, projectId: string, label: string, tokens: unknown): Promise<TokenSource> => {
    log.info('[api/tokenSources] uploading', { projectId, label });
    return fetchApi<TokenSource>(`/projects/${projectId}/token-sources`, {
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ label, tokens }),
      token: token ?? undefined,
    });
  },

  setEnabled: (token: string | null, projectId: string, sourceId: string, enabled: boolean): Promise<{ id: string; enabled: boolean }> =>
    fetchApi(`/projects/${projectId}/token-sources/${sourceId}`, {
      method: 'PATCH',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ enabled }),
      token: token ?? undefined,
    }),

  remove: (token: string | null, projectId: string, sourceId: string): Promise<{ deleted: boolean }> =>
    fetchApi(`/projects/${projectId}/token-sources/${sourceId}`, {
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
      token: token ?? undefined,
    }),
};
